import { useMemo } from 'react';
import { usePracticeFeatureFlags } from './usePracticeFeatureFlags';

/**
 * Per-agent task rollout. An agent's tasks are shown to a practice only when the Command Center has
 * switched on `agentTasks.<roleKey>` in `Practice.featureFlags` (backend
 * practices.service.ts agentTasksFlagKey) — one flat boolean per agent type, off by default.
 * Mirrors the backend reader exactly (same prefix, `true` only) so the two can never disagree.
 */
export const AGENT_TASKS_FLAG_PREFIX = 'agentTasks.';

export function enabledAgentTypesFromFlags(flags: Record<string, boolean>): Set<string> {
  const enabled = new Set<string>();
  for (const [key, value] of Object.entries(flags)) {
    if (value === true && key.startsWith(AGENT_TASKS_FLAG_PREFIX)) {
      enabled.add(key.slice(AGENT_TASKS_FLAG_PREFIX.length));
    }
  }
  return enabled;
}

const NONE: ReadonlySet<string> = new Set();

export interface EnabledAgentTypes {
  /** Null only while the practice's flags are still loading — "unknown", never "none". Once the
   *  fetch has settled, a missing or failed flag read is an EMPTY set: the gate fails closed. */
  enabled: ReadonlySet<string> | null;
  isLoading: boolean;
}

export function useEnabledAgentTypes(): EnabledAgentTypes {
  const { flags, isLoading } = usePracticeFeatureFlags();
  const enabled = useMemo(() => (flags ? enabledAgentTypesFromFlags(flags) : NONE), [flags]);
  return { enabled: isLoading ? null : enabled, isLoading };
}
